import { CareerLevel, ConsultantNode } from '../types/career';
import { PresetInfo, PresetStructureId } from '../types/simulation';
import { evaluateTreeQualifications } from '../math/qualification';

export const PRESET_STRUCTURES: PresetInfo[] = [
  {
    id: 'starting_trio',
    name: 'Стартовий рівень (Рівень 2)',
    description: 'Субагент 2-го рівня та 2 активних новачки 1-го рівня. Класична первинна база для побудови структури.',
    rootLevel: 2,
    teamSize: 3,
    expectedMonthlyAE: 150,
  },
  {
    id: 'level_4_team',
    name: 'Консультант 4-го рівня (~1 рік)',
    description: 'Старший менеджер 4-го рівня (~1 рік у бізнесі): 5 прямих гілок (2×Р3, 2×Р2, 1×Р1), 15 консультантів у команді.',
    rootLevel: 4,
    teamSize: 15,
    expectedMonthlyAE: 750,
  },
  {
    id: 'director_structure',
    name: 'Директорська структура (Рівень 6)',
    description: 'Директор 6-го рівня: 8 прямих гілок (2×Р5, 2×Р4, 2×Р3, 2×Р2), ~70 консультантів у команді. Директорський бонус +50 ₴/Е.',
    rootLevel: 6,
    teamSize: 69,
    expectedMonthlyAE: 3450,
  },
  {
    id: 'coowner_structure',
    name: 'Структура Співвласника (Рівень 8)',
    description: 'Президент / Співвласник 8-го рівня: 10 прямих гілок (2×Р7, 2×Р6, 2×Р5, 2×Р4, 2×Р3), ~150 консультантів у команді. Повний бонус лідера +150 ₴/Е.',
    rootLevel: 8,
    teamSize: 151,
    expectedMonthlyAE: 7550,
  },
];

interface NodeBuilderOptions {
  id: string;
  name: string;
  parentId: string | null;
  level?: CareerLevel;
  personalCumulativeAE: number;
  personalMonthlyAE?: number;
  monthlyMeetings?: number;
  conversionRate?: number;
  recruitmentRate?: number;
  children?: ConsultantNode[];
}

function makeNode({
  id,
  name,
  parentId,
  level = 1,
  personalCumulativeAE,
  personalMonthlyAE = 50,
  monthlyMeetings = 10,
  conversionRate = 0.20,
  recruitmentRate = 0.5,
  children = [],
}: NodeBuilderOptions): ConsultantNode {
  return {
    id,
    name,
    parentId,
    level,
    personalCumulativeAE,
    personalMonthlyAE,
    totalCumulativeAE: personalCumulativeAE,
    totalMonthlyAE: personalMonthlyAE,
    monthlyMeetings,
    conversionRate,
    recruitmentRate,
    isActive: true,
    children,
  };
}

/**
 * 1. Стартове тріо (Рівень 2):
 * Root (Level 2, cum >= 100 AE) + 2 Level 1 Direct Consultants (total 3 people).
 */
function createStartingTrioPreset(): ConsultantNode {
  const root = makeNode({
    id: 'trio-root',
    name: 'Олександр (Лідер)',
    parentId: null,
    level: 2,
    personalCumulativeAE: 150,
    personalMonthlyAE: 50,
    children: [
      makeNode({
        id: 'trio-child-1',
        name: 'Тетяна (Новачок 1)',
        parentId: 'trio-root',
        level: 1,
        personalCumulativeAE: 50,
        personalMonthlyAE: 50,
      }),
      makeNode({
        id: 'trio-child-2',
        name: 'Михайло (Новачок 2)',
        parentId: 'trio-root',
        level: 1,
        personalCumulativeAE: 50,
        personalMonthlyAE: 50,
      }),
    ],
  });

  return evaluateTreeQualifications(root);
}

/**
 * 2. Консультант 4-го рівня (~1 рік у бізнесі):
 * 5 direct branches: 2 at L3, 2 at L2, 1 at L1.
 * Total team size: exactly 15 consultants (Root + 14 downline).
 */
function createLevel4TeamPreset(): ConsultantNode {
  // Helper for Level 3 branch (leader + 3 L1 children = 4 people, cum >= 700, personal >= 300)
  const makeL3Branch = (id: string, name: string, parentId: string): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 3,
      personalCumulativeAE: 500,
      personalMonthlyAE: 50,
      children: [
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
        makeNode({ id: `${id}-c2`, name: `${name} (Н2)`, parentId: id, personalCumulativeAE: 80 }),
        makeNode({ id: `${id}-c3`, name: `${name} (Н3)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    });

  // Level 2 branch (leader + 2 L1 children = 3 people, cum >= 100)
  const l2Branch1 = makeNode({
    id: 'l4-b3',
    name: 'Сергій (Субагент 1)',
    parentId: 'l4-root',
    level: 2,
    personalCumulativeAE: 150,
    personalMonthlyAE: 50,
    children: [
      makeNode({ id: 'l4-b3-c1', name: 'Юлія (Н1)', parentId: 'l4-b3', personalCumulativeAE: 50 }),
      makeNode({ id: 'l4-b3-c2', name: 'Іван (Н2)', parentId: 'l4-b3', personalCumulativeAE: 50 }),
    ],
  });

  // Level 2 branch (leader + 1 L1 child = 2 people, cum >= 100)
  const l2Branch2 = makeNode({
    id: 'l4-b4',
    name: 'Анна (Субагент 2)',
    parentId: 'l4-root',
    level: 2,
    personalCumulativeAE: 150,
    personalMonthlyAE: 50,
    children: [
      makeNode({ id: 'l4-b4-c1', name: 'Вікторія (Н1)', parentId: 'l4-b4', personalCumulativeAE: 50 }),
    ],
  });

  // Level 1 branch (single consultant, 1 person, cum < 100)
  const l1Branch = makeNode({
    id: 'l4-b5',
    name: 'Роман (Консультант)',
    parentId: 'l4-root',
    level: 1,
    personalCumulativeAE: 80,
    personalMonthlyAE: 50,
    children: [],
  });

  const root = makeNode({
    id: 'l4-root',
    name: 'Андрій (Старший менеджер)',
    parentId: null,
    level: 4,
    personalCumulativeAE: 1000,
    personalMonthlyAE: 50,
    children: [
      makeL3Branch('l4-b1', 'Олена (Менеджер 1)', 'l4-root'),
      makeL3Branch('l4-b2', 'Дмитро (Менеджер 2)', 'l4-root'),
      l2Branch1,
      l2Branch2,
      l1Branch,
    ],
  });

  return evaluateTreeQualifications(root);
}

/**
 * 3. Директорська структура (Рівень 6):
 * 8 direct branches: 2 at L5, 2 at L4, 2 at L3, 2 at L2.
 * Total team size: 69 consultants (Root + 68 downline, within 60–80 range).
 * Director bonus (+50 грн/АЕ) applies on all lines <= L5.
 */
function createDirectorStructurePreset(): ConsultantNode {
  // L2 building block (leader + 2 L1s = 3 people)
  const makeL2 = (id: string, name: string, parentId: string, baseAE = 150): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 2,
      personalCumulativeAE: baseAE,
      personalMonthlyAE: 50,
      children: [
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 50 }),
        makeNode({ id: `${id}-c2`, name: `${name} (Н2)`, parentId: id, personalCumulativeAE: 50 }),
      ],
    });

  // L3 building block (leader + 3 L1s = 4 people, cum >= 700, personal >= 300)
  const makeL3 = (id: string, name: string, parentId: string, baseAE = 500): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 3,
      personalCumulativeAE: baseAE,
      personalMonthlyAE: 50,
      children: [
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
        makeNode({ id: `${id}-c2`, name: `${name} (Н2)`, parentId: id, personalCumulativeAE: 80 }),
        makeNode({ id: `${id}-c3`, name: `${name} (Н3)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    });

  // L4 building block (leader + 2 x L2 + 1 x L1 = 8 people, cum >= 3000, personal >= 300, satisfies [2, 2])
  const makeL4 = (id: string, name: string, parentId: string, baseAE = 2500): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 4,
      personalCumulativeAE: baseAE,
      personalMonthlyAE: 50,
      children: [
        makeL2(`${id}-l2a`, `${name} Г1`, id),
        makeL2(`${id}-l2b`, `${name} Г2`, id),
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    });

  // L5 building block (leader + 2 x L3 + 3 x L2 + 1 x L1 = 19 people, cum >= 10000, personal >= 300, satisfies [3, 3, 2])
  const makeL5 = (id: string, name: string, parentId: string, baseAE = 8000): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 5,
      personalCumulativeAE: baseAE,
      personalMonthlyAE: 50,
      children: [
        makeL3(`${id}-l3a`, `${name} Г1`, id),
        makeL3(`${id}-l3b`, `${name} Г2`, id),
        makeL2(`${id}-l2a`, `${name} Г3`, id),
        makeL2(`${id}-l2b`, `${name} Г4`, id),
        makeL2(`${id}-l2c`, `${name} Г5`, id),
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    });

  const root = makeNode({
    id: 'l6-root',
    name: 'Володимир (Директор)',
    parentId: null,
    level: 6,
    personalCumulativeAE: 2000,
    personalMonthlyAE: 50,
    children: [
      // 2 x Level 5 (19 + 19 = 38 people)
      makeL5('l6-b1', 'Ольга (Управлінець 1)', 'l6-root'),
      makeL5('l6-b2', 'Василь (Управлінець 2)', 'l6-root'),
      // 2 x Level 4 (8 + 8 = 16 people)
      makeL4('l6-b3', 'Світлана (Ст. Менеджер 1)', 'l6-root'),
      makeL4('l6-b4', 'Максим (Ст. Менеджер 2)', 'l6-root'),
      // 2 x Level 3 (4 + 4 = 8 people)
      makeL3('l6-b5', 'Ірина (Менеджер 1)', 'l6-root'),
      makeL3('l6-b6', 'Тарас (Менеджер 2)', 'l6-root'),
      // 2 x Level 2 (3 + 3 = 6 people)
      makeL2('l6-b7', 'Катерина (Субагент 1)', 'l6-root'),
      makeL2('l6-b8', 'Богдан (Субагент 2)', 'l6-root'),
    ],
  });

  return evaluateTreeQualifications(root);
}

/**
 * 4. Структура Співвласника (Рівень 8):
 * 10 direct branches (all paired from L7 down: 2×Р7, 2×Р6, 2×Р5, 2×Р4, 2×Р3).
 * Total team size: 151 consultants (~150 people).
 * Director bonus (+50) & Co-owner bonus (+100) are paid on branches <= L5.
 */
function createCoownerStructurePreset(): ConsultantNode {
  // Helpers
  const makeL2 = (id: string, name: string, parentId: string): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 2,
      personalCumulativeAE: 150,
      personalMonthlyAE: 50,
      children: [
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 50 }),
      ],
    }); // 2 people

  const makeL3 = (id: string, name: string, parentId: string): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 3,
      personalCumulativeAE: 500,
      personalMonthlyAE: 50,
      children: [
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
        makeNode({ id: `${id}-c2`, name: `${name} (Н2)`, parentId: id, personalCumulativeAE: 80 }),
        makeNode({ id: `${id}-c3`, name: `${name} (Н3)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    }); // 4 people

  const makeL4 = (id: string, name: string, parentId: string): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 4,
      personalCumulativeAE: 2600,
      personalMonthlyAE: 50,
      children: [
        makeL2(`${id}-l2a`, `${name} Г1`, id),
        makeL2(`${id}-l2b`, `${name} Г2`, id),
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    }); // 1 + 2 + 2 + 1 = 6 people

  const makeL5 = (id: string, name: string, parentId: string): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 5,
      personalCumulativeAE: 8500,
      personalMonthlyAE: 50,
      children: [
        makeL3(`${id}-l3a`, `${name} Г1`, id), // 4 people
        makeL3(`${id}-l3b`, `${name} Г2`, id), // 4 people
        makeL2(`${id}-l2a`, `${name} Г3`, id), // 2 people
      ],
    }); // 1 + 4 + 4 + 2 = 11 people

  const makeL6 = (id: string, name: string, parentId: string): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 6,
      personalCumulativeAE: 18000,
      personalMonthlyAE: 50,
      children: [
        makeL4(`${id}-l4a`, `${name} Г1`, id), // 6 people
        makeL4(`${id}-l4b`, `${name} Г2`, id), // 6 people
        makeL3(`${id}-l3a`, `${name} Г3`, id), // 4 people
        makeL2(`${id}-l2a`, `${name} Г4`, id), // 2 people
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    }); // 1 + 6 + 6 + 4 + 2 + 1 = 20 people

  const makeL7 = (id: string, name: string, parentId: string): ConsultantNode =>
    makeNode({
      id,
      name,
      parentId,
      level: 7,
      personalCumulativeAE: 40000,
      personalMonthlyAE: 50,
      children: [
        makeL5(`${id}-l5a`, `${name} Г1`, id), // 11 people
        makeL5(`${id}-l5b`, `${name} Г2`, id), // 11 people
        makeL4(`${id}-l4a`, `${name} Г3`, id), // 6 people
        makeL3(`${id}-l3a`, `${name} Г4`, id), // 4 people
        makeNode({ id: `${id}-c1`, name: `${name} (Н1)`, parentId: id, personalCumulativeAE: 80 }),
      ],
    }); // 1 + 11 + 11 + 6 + 4 + 1 = 34 people

  const root = makeNode({
    id: 'l8-root',
    name: 'Президент / Співвласник',
    parentId: null,
    level: 8,
    personalCumulativeAE: 35000,
    personalMonthlyAE: 50,
    children: [
      // 2 x Level 7 (34 + 34 = 68 people)
      makeL7('l8-b1', 'Олександр (Золотий Директор 1)', 'l8-root'),
      makeL7('l8-b2', 'Олена (Золотий Директор 2)', 'l8-root'),
      // 2 x Level 6 (20 + 20 = 40 people)
      makeL6('l8-b3', 'Михайло (Директор 1)', 'l8-root'),
      makeL6('l8-b4', 'Тетяна (Директор 2)', 'l8-root'),
      // 2 x Level 5 (11 + 11 = 22 people)
      makeL5('l8-b5', 'Андрій (Управлінець 1)', 'l8-root'),
      makeL5('l8-b6', 'Наталія (Управлінець 2)', 'l8-root'),
      // 2 x Level 4 (6 + 6 = 12 people)
      makeL4('l8-b7', 'Сергій (Менеджер 1)', 'l8-root'),
      makeL4('l8-b8', 'Юлія (Менеджер 2)', 'l8-root'),
      // 2 x Level 3 (4 + 4 = 8 people)
      makeL3('l8-b9', 'Дмитро (Менеджер 1)', 'l8-root'),
      makeL3('l8-b10', 'Оксана (Менеджер 2)', 'l8-root'),
    ],
  });

  return evaluateTreeQualifications(root);
}

/**
 * Returns a clone of the specified preset tree structure.
 */
export function getPresetTree(id: PresetStructureId): ConsultantNode {
  switch (id) {
    case 'starting_trio':
      return createStartingTrioPreset();
    case 'level_4_team':
      return createLevel4TeamPreset();
    case 'director_structure':
      return createDirectorStructurePreset();
    case 'coowner_structure':
      return createCoownerStructurePreset();
    default:
      return createStartingTrioPreset();
  }
}
